import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchCommercialPlansCatalog, runCommercialOnboarding } from "@/lib/platformApi";
import {
  billingCadenceLabel,
  commercialPlanModuleIds,
  commercialPlanOption,
  commercialPlanOptions,
} from "@/lib/commercialPlanUi";

const STEPS = ["Organización", "Licencia", "Administrador", "Resumen"];

export default function PlatformOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [plansCatalog, setPlansCatalog] = useState(null);
  const [plansError, setPlansError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [orgName, setOrgName] = useState("");
  const [orgRfc, setOrgRfc] = useState("");
  const [validFrom, setValidFrom] = useState("2026-01-01");
  const [validTo, setValidTo] = useState("2027-12-31");
  const [commercialPlan, setCommercialPlan] = useState("");
  const [billingCadence, setBillingCadence] = useState("annual");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  useEffect(() => {
    fetchCommercialPlansCatalog()
      .then((catalog) => {
        setPlansCatalog(catalog);
        const firstPlan = commercialPlanOptions(catalog)[0]?.id || "";
        setCommercialPlan((current) => current || firstPlan);
      })
      .catch(() => setPlansError("No se pudo cargar la matriz publicada de planes."));
  }, []);

  const planOptions = commercialPlanOptions(plansCatalog);
  const selectedPlan = commercialPlanOption(plansCatalog, commercialPlan);

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await runCommercialOnboarding({
        organization: { name: orgName, rfc: orgRfc },
        license: {
          valid_from: validFrom,
          valid_to: validTo,
          commercial_plan: commercialPlan,
          billing_cadence: billingCadence,
        },
        admin: { email: adminEmail, name: adminName, password: adminPassword },
      });
      toast.success(`Alta completada: ${result.organization?.name}`);
      navigate("/platform/license-requests");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en alta comercial");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-[#0F2D5C]">Alta rápida comercial</h2>
        <p className="mt-1 text-sm text-slate-500">
          Organización, licencia y administrador en un solo flujo.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {STEPS.map((label, idx) => (
            <span
              key={label}
              className={`rounded-full px-3 py-1 border ${
                idx === step
                  ? "border-[#0F2D5C] bg-[#0F2D5C]/10 text-[#0F2D5C] font-medium"
                  : idx < step
                    ? "border-slate-200 text-slate-600"
                    : "border-slate-100 text-slate-400"
              }`}
            >
              {idx + 1}. {label}
            </span>
          ))}
        </div>
      </section>

      {step === 0 && (
        <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm space-y-3">
          <div>
            <Label>Nombre de la organización</Label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
          </div>
          <div>
            <Label>RFC</Label>
            <Input value={orgRfc} onChange={(e) => setOrgRfc(e.target.value.toUpperCase())} required />
          </div>
        </section>
      )}

      {step === 1 && (
        <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Vigencia desde</Label>
              <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            </div>
            <div>
              <Label>Vigencia hasta</Label>
              <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Plan comercial</Label>
            {plansError ? (
              <p className="mt-1 text-xs text-red-700">{plansError}</p>
            ) : (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {planOptions.map((plan) => (
                  <label
                    key={plan.id}
                    className={`cursor-pointer rounded-md border p-3 text-sm ${
                      commercialPlan === plan.id
                        ? "border-[#0F2D5C] bg-[#0F2D5C]/5"
                        : "border-slate-200 bg-white"
                    }`}
                    data-testid={`platform-onboarding-plan-${plan.id}`}
                  >
                    <span className="flex items-center gap-2 font-semibold text-[#0F2D5C]">
                      <input
                        type="radio"
                        name="commercial-plan"
                        checked={commercialPlan === plan.id}
                        onChange={() => setCommercialPlan(plan.id)}
                      />
                      {plan.label}
                    </span>
                    <span className="mt-1 block text-xs text-slate-600">
                      {commercialPlanModuleIds(plan).join(" · ")}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">
                      Hasta {plan.limits?.authorized_users ?? "—"} usuarios · {plan.limits?.authorized_clients ?? "—"} clientes
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="max-w-xs">
            <Label>Cadencia de cobro</Label>
            <Select value={billingCadence} onValueChange={setBillingCadence}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensual</SelectItem>
                <SelectItem value="annual">Anual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-slate-500 m-0">
            Los módulos y límites se derivan de la matriz publicada. Los ajustes manuales se reservan para soporte.
          </p>
        </section>
      )}

      {step === 2 && (
        <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm space-y-3">
          <div>
            <Label>Correo del administrador</Label>
            <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} required />
          </div>
          <div>
            <Label>Nombre</Label>
            <Input value={adminName} onChange={(e) => setAdminName(e.target.value)} required />
          </div>
          <div>
            <Label>Contraseña inicial</Label>
            <Input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              minLength={6}
              required
            />
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm text-sm space-y-2">
          <p><strong>Organización:</strong> {orgName} ({orgRfc})</p>
          <p><strong>Licencia:</strong> {validFrom} — {validTo}</p>
          <p><strong>Plan:</strong> {selectedPlan?.label || commercialPlan || "—"} · {billingCadenceLabel(billingCadence)}</p>
          <p><strong>Incluye:</strong> {commercialPlanModuleIds(selectedPlan).join(", ") || "—"}</p>
          <p><strong>Administrador:</strong> {adminName} &lt;{adminEmail}&gt;</p>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        {step > 0 && (
          <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)}>
            Anterior
          </Button>
        )}
        {step < STEPS.length - 1 && (
          <Button
            type="button"
            className="bg-[#0F2D5C] hover:bg-[#0a2247]"
            onClick={() => setStep((s) => s + 1)}
            disabled={
              (step === 0 && (!orgName.trim() || orgRfc.length < 10)) ||
              (step === 1 && (!commercialPlan || !selectedPlan)) ||
              (step === 2 && (!adminEmail || !adminName || adminPassword.length < 6))
            }
          >
            Siguiente
          </Button>
        )}
        {step === STEPS.length - 1 && (
          <Button
            type="button"
            className="bg-[#0F2D5C] hover:bg-[#0a2247]"
            onClick={onSubmit}
            disabled={submitting || !commercialPlan || !selectedPlan}
          >
            {submitting ? "Creando…" : "Confirmar alta"}
          </Button>
        )}
        <Button type="button" variant="ghost" asChild>
          <Link to="/platform/license-requests">Cancelar</Link>
        </Button>
      </div>
    </div>
  );
}
